import { Link } from "react-router-dom";
import { useState } from "react";
import { Sparkles, Share2, MessageCircle, Twitter, Activity, MessageCircleQuestion } from "lucide-react";
import { Img } from "./NewsCard";
import VerifiedBadge from "./VerifiedBadge";
import PulseModal from "./PulseModal";
import AskReporterModal from "./AskReporterModal";
import PricingModal from "./PricingModal";

const CATEGORY_LABEL = {
  top: "TOP STORY",
  government: "POLITICS",
  economy: "BUSINESS",
  markets: "MARKETS",
  world: "WORLD",
  technology: "TECH",
  health: "HEALTH",
};

export default function HeroCard({ article, onBrief, onShare }) {
  const [showPulse, setShowPulse] = useState(false);
  const [showAsk, setShowAsk] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  if (!article) return null;
  const cat = CATEGORY_LABEL[article.category] || article.category?.toUpperCase();
  return (
    <article className="hero-card" data-testid={`hero-${article.id}`}>
      <Link to={`/article/${article.id}`} className="image">
        <Img src={article.image} category={article.category} alt={article.title} headline={article.title} source={article.source} />
      </Link>
      <div className="body">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="cat">{cat}</span>
          {article.right_radar && (
            <span className="text-[9px] uppercase tracking-[0.16em] font-black text-white bg-rose-600 px-1.5 py-0.5 rounded" data-testid={`hero-right-radar-${article.id}`}>
              Trending Right
            </span>
          )}
        </div>
        <Link to={`/article/${article.id}`} className="title hover:text-[var(--blue)]" data-testid={`hero-title-${article.id}`}>
          {article.title}
        </Link>
        {article.narrative && (
          <p className="text-[13.5px] text-[var(--ink)] leading-snug mt-2 border-l-2 border-[var(--gold)] pl-3" data-testid={`hero-narrative-${article.id}`}>
            <span className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--gold)] mr-2">Why it matters</span>
            {article.narrative.slice(0, 260)}{article.narrative.length > 260 ? "…" : ""}
          </p>
        )}
        {article.brief_preview && article.brief_preview !== article.narrative && (
          <p className="summary mt-2">{article.brief_preview.slice(0, 220)}{article.brief_preview.length > 220 ? "…" : ""}</p>
        )}
        <div className="flex flex-wrap gap-2 items-center text-[12px] text-[var(--muted)]">
          {article.avatar?.portrait && (
            <img src={article.avatar.portrait} alt={article.avatar.name} className="w-7 h-7 rounded-full" data-testid={`hero-avatar-thumb-${article.id}`} />
          )}
          <span className="font-black text-[var(--ink)]">
            {article.avatar?.name ? `By ${article.avatar.name}` : "Newseze Wire"}
          </span>
          {article.avatar?.role && <span>· {article.avatar.role}</span>}
          <span>·</span>
          <span>{new Date(article.published).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
        </div>
        {article.source && (
          <div className="text-[10.5px] text-[var(--muted)] mt-1 flex items-center gap-1.5" data-testid={`hero-wire-${article.id}`}>
            <span className="uppercase tracking-[0.14em] font-bold opacity-70">Wire</span>
            <span>{article.source}</span>
            <VerifiedBadge name={article.source} compact testId={`hero-verified-${article.id}`} />
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-2">
          <Link to={`/article/${article.id}`} className="btn primary" data-testid={`hero-read-${article.id}`}>
            Read full story →
          </Link>
          <button className="btn" onClick={() => setShowPulse(true)} data-testid={`hero-pulse-${article.id}`} title="Pulse — what this means for you">
            <Activity size={14} /> Pulse
          </button>
          <button className="btn" onClick={() => setShowAsk(true)} data-testid={`hero-ask-${article.id}`} title="Questeze — ask the Newseze reporter">
            <MessageCircleQuestion size={14} /> Questeze
          </button>
          <button className="btn" onClick={() => onBrief?.(article)} data-testid={`hero-brief-${article.id}`}>
            <Sparkles size={14} /> Brief me
          </button>
          <Link to={`/article/${article.id}#ask-us-section`} className="btn" data-testid={`hero-comment-${article.id}`}>
            <MessageCircle size={14} /> Comment
          </Link>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title + " via @Newseze")}&url=${encodeURIComponent(typeof window !== "undefined" ? `${window.location.origin}/article/${article.id}` : `/article/${article.id}`)}`}
            target="_blank"
            rel="nofollow noopener noreferrer"
            referrerPolicy="no-referrer"
            className="btn"
            data-testid={`hero-x-share-${article.id}`}
          >
            <Twitter size={14} /> Post to X
          </a>
          <button className="btn" onClick={() => onShare?.(article)} data-testid={`hero-share-${article.id}`}>
            <Share2 size={14} /> Share
          </button>
        </div>
      </div>
      {showPulse && (
        <PulseModal
          article={article}
          onClose={() => setShowPulse(false)}
          onUpgrade={() => { setShowPulse(false); setShowPricing(true); }}
        />
      )}
      {showAsk && (
        <AskReporterModal
          article={article}
          onClose={() => setShowAsk(false)}
          onUpgrade={() => { setShowAsk(false); setShowPricing(true); }}
        />
      )}
      {showPricing && <PricingModal onClose={() => setShowPricing(false)} />}
    </article>
  );
}
